/**
 * The portal's sections, their pages and what each page lets a role do: one list for the side menu and
 * the roles page (owner request 2026-11-01), so a new section, page or permission shows in both at once.
 *
 * Every permission is an action of exactly one page; the type check below and sections.test.ts fail when
 * a permission is added to @mubin/contracts without a place here. UI only: the server authorizes again.
 */
import {
  Award,
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  CalendarCheck,
  CalendarClock,
  CircleDot,
  ClipboardList,
  HandHeart,
  Headphones,
  History,
  Layers,
  Mic,
  Settings,
  ShieldCheck,
  Smartphone,
  UserCheck,
  UserPlus,
  UserX,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { Permission } from "@mubin/contracts";

export interface PermissionAction {
  key: Permission;
  label: string;
}
export interface SectionPage {
  to: string;
  label: string;
  icon: LucideIcon;
  /** What opens the page in the menu: a permission, or only a full administrator. */
  opensWith: Permission | "fullAdmin";
  /** The permissions that belong to this page, as the roles page lists them. */
  actions: readonly PermissionAction[];
}
export interface Section {
  id: string;
  label: string;
  pages: readonly SectionPage[];
}

export const SECTIONS = [
  {
    id: "teaching",
    label: "التعليم اليومي",
    pages: [
      {
        to: "/circles",
        label: "الحلقات",
        icon: CircleDot,
        opensWith: "circles.view",
        actions: [
          { key: "circles.view", label: "عرض المجمعات والحلقات" },
          { key: "circles.manage", label: "إنشاء الحلقات وتعديلها وإسناد المعلمين" },
          { key: "enrollments.manage", label: "تسجيل الطلاب في الحلقات ونقلهم وإنهاء تسجيلهم" },
        ],
      },
      {
        to: "/recitation",
        label: "التسميع",
        icon: Mic,
        opensWith: "recitation.view",
        actions: [
          { key: "recitation.view", label: "عرض سجل التسميع" },
          { key: "results.revise", label: "تعديل نتائج التسميع مع ذكر السبب" },
        ],
      },
      {
        to: "/attendance",
        label: "الحضور",
        icon: CalendarCheck,
        opensWith: "attendance.view",
        actions: [
          { key: "attendance.view", label: "عرض الحضور" },
          { key: "attendance.edit", label: "تسجيل الحضور وتصحيحه" },
        ],
      },
      {
        to: "/plans",
        label: "الخطط",
        icon: ClipboardList,
        opensWith: "plans.view",
        actions: [
          { key: "plans.view", label: "عرض الخطط" },
          { key: "plans.edit", label: "إنشاء الخطط وتعديلها" },
          { key: "plans.approve", label: "اعتماد الخطط" },
        ],
      },
      {
        to: "/exams",
        label: "الاختبارات",
        icon: Award,
        opensWith: "exams.view",
        actions: [
          { key: "exams.view", label: "عرض الاختبارات" },
          { key: "exams.manage", label: "جدولة الاختبارات وتسجيلها وإلغاؤها" },
          { key: "exams.approve", label: "اعتماد نتائج الاختبارات" },
        ],
      },
    ],
  },
  {
    id: "people",
    label: "الأشخاص",
    pages: [
      {
        to: "/users",
        label: "المستخدمون",
        icon: Users,
        opensWith: "users.view",
        actions: [
          { key: "users.view", label: "عرض المستخدمين" },
          { key: "users.manage", label: "إنشاء الحسابات وتعديلها وتعيين كلمات المرور" },
          { key: "users.roles", label: "منح الأدوار وتعديلها وسحبها" },
        ],
      },
      {
        to: "/new-students",
        label: "الطلاب الجدد",
        icon: UserCheck,
        opensWith: "join.manage",
        actions: [],
      },
      {
        to: "/join-requests",
        label: "طلبات الانضمام",
        icon: UserPlus,
        opensWith: "join.manage",
        actions: [{ key: "join.manage", label: "قبول طلبات الانضمام، وإسناد الطلاب الجدد لحلقاتهم" }],
      },
    ],
  },
  {
    id: "volunteering",
    label: "التطوع",
    pages: [
      {
        to: "/volunteers",
        label: "طلبات التطوع",
        icon: HandHeart,
        opensWith: "volunteers.manage",
        actions: [
          {
            key: "volunteers.manage",
            label: "عرض الطلبات والبت فيها ومراسلة المتقدمين ودعوتهم للمقابلة",
          },
        ],
      },
      {
        to: "/interviews",
        label: "قاعة المقابلات",
        icon: Headphones,
        opensWith: "interviews.conduct",
        actions: [{ key: "interviews.conduct", label: "إجراء المقابلات وتذكير المتقدمين وتسجيل نتيجتها" }],
      },
      {
        to: "/interview-slots",
        label: "مواعيد المقابلات",
        icon: CalendarClock,
        opensWith: "interviews.slots",
        actions: [{ key: "interviews.slots", label: "إضافة مواعيد المقابلات وحذفها" }],
      },
    ],
  },
  {
    id: "reports",
    label: "التقارير والتواصل",
    pages: [
      {
        to: "/reports",
        label: "التقارير",
        icon: BarChart3,
        opensWith: "reports.view",
        actions: [{ key: "reports.view", label: "عرض التقارير وتصديرها" }],
      },
      {
        to: "/notify",
        label: "إرسال إشعار",
        icon: Bell,
        opensWith: "notifications.send",
        actions: [{ key: "notifications.send", label: "إرسال الإشعارات" }],
      },
    ],
  },
  {
    id: "structure",
    label: "الهيكل والبرامج",
    pages: [
      {
        to: "/branches",
        label: "المجمعات",
        icon: Building2,
        opensWith: "circles.view",
        actions: [{ key: "branches.manage", label: "تعديل بيانات المجمع" }],
      },
      {
        to: "/programs",
        label: "البرامج",
        icon: Layers,
        opensWith: "programs.manage",
        actions: [{ key: "programs.manage", label: "إدارة البرامج" }],
      },
      {
        to: "/policies",
        label: "سياسات البرامج",
        icon: BookOpen,
        opensWith: "policies.manage",
        actions: [{ key: "policies.manage", label: "تعديل سياسات البرامج" }],
      },
    ],
  },
  {
    id: "admin",
    label: "الإدارة",
    pages: [
      { to: "/roles", label: "الأدوار والصلاحيات", icon: ShieldCheck, opensWith: "fullAdmin", actions: [] },
      {
        to: "/settings",
        label: "الإعدادات",
        icon: Settings,
        opensWith: "settings.manage",
        actions: [{ key: "settings.manage", label: "الإعدادات (القوائم والإجازات وأنواع الاختبارات)" }],
      },
      {
        to: "/app-release",
        label: "تطبيق أندرويد",
        icon: Smartphone,
        opensWith: "settings.manage",
        actions: [],
      },
      {
        to: "/audit",
        label: "سجل التدقيق",
        icon: History,
        opensWith: "audit.view",
        actions: [{ key: "audit.view", label: "عرض سجل التدقيق" }],
      },
      {
        to: "/deletion-requests",
        label: "طلبات حذف الحساب",
        icon: UserX,
        opensWith: "deletion.manage",
        actions: [{ key: "deletion.manage", label: "البت في طلبات حذف الحساب" }],
      },
    ],
  },
] as const satisfies readonly Section[];

/** Fails to compile when a permission has no page here. */
type Placed = (typeof SECTIONS)[number]["pages"][number]["actions"][number]["key"];
const everyPermissionPlaced: [Exclude<Permission, Placed>] extends [never] ? true : false = true;
void everyPermissionPlaced;

/** Every action with its page, in menu order. */
export const PERMISSION_ACTIONS: (PermissionAction & { page: string; section: string })[] = SECTIONS.flatMap(
  (s: Section) => s.pages.flatMap((p) => p.actions.map((a) => ({ ...a, page: p.label, section: s.label }))),
);

export const PERMISSION_LABEL = Object.fromEntries(
  PERMISSION_ACTIONS.map((a) => [a.key, a.label]),
) as Readonly<Record<string, string>>;
